// Returns a new object with the same fields but every value mapped according to the mapping function

export const objMap = <T, O extends Record<string, T>, V>(
  obj: O,
  func: (obj: T, key: keyof O) => V,
): Record<keyof O, V> => {
  const records = {} as Record<keyof O, V>;
  Object.keys(obj).forEach((key: keyof O) => {
    records[key] = func(obj[key], key);
  });
  return records;
};

// Returns a list of objects generated by they key value pairs on the source object
export const objMapToList = <T, V>(
  obj: Record<string, T>,
  func: (obj: T, key: string) => V,
): V[] => {
  return Object.keys(obj).map((key) => func(obj[key], key));
};

export const objFilter = <T>(
  obj: Record<string, T>,
  func: (obj: T, key: string) => boolean,
): Record<string, T> => {
  const ret: Record<string, T> = {};
  objMapToList(obj, (val, key) => {
    if (func(val, key)) {
      ret[key] = val;
    }
  });
  return ret;
};

// Returns the list of values contained in the object
export const valuesOf = <T>(obj: Record<string, T>): T[] => {
  return Object.keys(obj).map((x) => obj[x]);
};

export function fieldMap<T, K extends keyof T>(
  fields: K[],
  mapper: (field: K) => T[K],
): Pick<T, K> {
  return fields.reduce<{ [P in K]: T[P] }>(
    (rec, key) => {
      rec[key] = mapper(key);
      return rec;
      // tslint:disable-next-line:no-object-literal-type-assertion
    },
    {} as Pick<T, K>,
  );
}

export const removeUndefined = (
  src: Record<string, any>,
): Record<string, any> => objFilter(src, (x) => x !== undefined);

export const createLookupMapFor = <O>(
  objs: O[],
  getter: (o: O) => string,
): Record<string, O> => {
  const ret: Record<string, O> = {};
  objs.forEach((obj) => {
    ret[getter(obj)] = obj;
  });
  return ret;
};

export const createIndexLookupMapFor = <O>(
  objs: O[],
  getter: (o: O) => string,
): Record<string, number> => {
  const ret: Record<string, number> = {};
  objs.forEach((obj, i) => {
    ret[getter(obj)] = i;
  });
  return ret;
};

export const createObjFromArray = <O, T>(
  objs: O[],
  getter: (o: O) => string,
  mapper: (o: O) => T,
  filter?: (x: T) => boolean,
): Record<string, T> => {
  const ret: Record<string, T> = {};
  objs.forEach((obj) => {
    const x = mapper(obj);
    if (!filter || filter(x)) {
      ret[getter(obj)] = x;
    }
  });
  return ret;
};

export const getChanges = <O extends object>(x: O, y: O): Partial<O> => {
  const ret: Partial<O> = {};
  const allKeys = new Set<keyof O>();
  Object.keys(x)
    .concat(...Object.keys(y))
    .forEach((key) => allKeys.add(key as keyof O));
  Array.from(allKeys).forEach((key) => {
    if (x[key] !== y[key]) {
      ret[key] = y[key];
    }
  });
  return ret;
};

export const isEmptyObject = (obj: object) => {
  return obj && typeof obj === "object" && Object.keys(obj).length === 0;
};

export const createLookupMapAndCheckDuplicates = <O>(
  objs: O[],
  getter: (o: O) => string,
): [Record<string, O>, boolean] => {
  const ret: Record<string, O> = {};
  let duplicates = false;
  objs.forEach((obj) => {
    const key = getter(obj);
    if (ret[key] != null) {
      duplicates = true;
    }
    ret[key] = obj;
  });
  return [ret, duplicates];
};

export const createIndexFor = <O>(
  objs: O[],
  getter: (o: O) => string,
): Record<string, O[]> => {
  const ret: Record<string, O[]> = {};
  objs.forEach((obj) => {
    if (ret[getter(obj)] == null) {
      ret[getter(obj)] = [obj];
    } else {
      ret[getter(obj)].push(obj);
    }
  });
  return ret;
};

export const getFilterAndOpposite = <O>(
  objs: O[],
  filter: (o: O) => boolean,
): [O[], O[]] => {
  const filtered: O[] = [];
  const opposite: O[] = [];
  objs.forEach((obj) => {
    if (filter(obj)) {
      filtered.push(obj);
    } else {
      opposite.push(obj);
    }
  });
  return [filtered, opposite];
};
